import React, { useEffect, useState } from 'react';

import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { camelize } from '../../util/string';
import { propTypes } from '../../util/types';
import { isInstructorUser } from '../../util/userHelpers';
import { getFaqSchema, getPageMeta } from '../../util/pageAssets';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { LayoutSingleColumn, NamedRedirect, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import { ASSET_NAME } from './LandingPage.duck';
import DriveloHome, { FAQ_KEYS } from './DriveloHome/DriveloHome';

export const LandingPageComponent = props => {
  const { pageAssetsData, currentUser, scrollingDisabled } = props;
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Drivelo: logged-in instructors go straight to their dashboard. The current user is only
  // fetched client-side, so wait until mounted to keep the SSR markup and hydration in sync.
  const isInstructor = !!currentUser?.id && isInstructorUser(currentUser);
  if (mounted && isInstructor) {
    return <NamedRedirect name="InstructorDashboardPage" />;
  }

  // Drivelo: the homepage layout and copy live in code (DriveloHome). The Console landing-page
  // asset still supplies the photos and the SEO title, description and share image.
  const assetData = pageAssetsData?.[camelize(ASSET_NAME)]?.data;
  const meta = getPageMeta(assetData?.meta);
  const { marketplaceName } = config;
  const title =
    meta.title || intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { marketplaceName });
  const description =
    meta.description || intl.formatMessage({ id: 'LandingPage.schemaDescription' });

  const faqs = FAQ_KEYS.map(key => ({
    question: intl.formatMessage({ id: `LandingPage.faq${key}Question` }),
    answer: intl.formatMessage({ id: `LandingPage.faq${key}Answer` }),
  }));
  const schema = [
    { '@context': 'http://schema.org', '@type': 'WebPage', name: title, description },
    getFaqSchema(faqs),
  ];

  return (
    <Page
      title={title}
      description={description}
      socialSharing={meta.socialSharing}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <DriveloHome sections={assetData?.sections} />
      </LayoutSingleColumn>
    </Page>
  );
};

LandingPageComponent.propTypes = {
  pageAssetsData: object,
  currentUser: propTypes.currentUser,
  scrollingDisabled: bool,
};

const mapStateToProps = state => {
  const { pageAssetsData } = state.hostedAssets || {};
  const { currentUser } = state.user;

  return {
    pageAssetsData,
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps))(LandingPageComponent);

export default LandingPage;
